import {
    Box,
    Grid,
    Theme,
    Typography
} from "@mui/material";
import CircleIcon from '@mui/icons-material/Circle';
import {
    useEffect,
    useState
} from "react";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { GetMarketCapAllocationResponseBody } from "../../../../../redux-store/types/analytics";
import { RootStateType } from "../../../../../redux-store/reducers";
import { MarketCapAllocationProps } from "../types";
import { getMarketCapAllocation } from "../../../../../redux-store/actions/analytics";
import { MarketCapAllocationLoading } from "../../../loadingComponents";
import { NO_DATA_AVAILABLE } from "../../../../../utils/constant";

const colorMap = ["#0D3C6B", "#00A76F", "#00B8D9", "FFAB00"]

const assetGroupMap: { [key: string]: string; } = {
    "Mutual Fund": "MF"
}

export default function MarketCapAllocation({
    clientCode,
    portfolioId,
    assetGroup
}: MarketCapAllocationProps): JSX.Element {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState<boolean>(false)
    const [data, setData] = useState<GetMarketCapAllocationResponseBody>({} as GetMarketCapAllocationResponseBody)

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchData = async (
        clientCode: string,
        portfolioId: string,
        selectedAssetGroup = "MF") => {
        try {
            setLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId,
                assetGroup: assetGroupMap[selectedAssetGroup],
            }

            const response = await (dispatch(getMarketCapAllocation(payload)) as unknown as Promise<GetMarketCapAllocationResponseBody>);


            setData(response)

        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData(
            clientCode,
            portfolioId,
            assetGroup
        )
    }, [clientCode,
        portfolioId,
        assetGroup])

    const modifiedData = {
        ...data,
        result: {
            ...data?.result ?? {},
            labelData: data?.result?.labelData?.map((obj, index) => ({
                ...obj,
                backgroundColor: colorMap[index]
            })) ?? []
        }
    }

    const modifiedLabelData = modifiedData?.result?.labelData ?? []

    if (isLoading) {
        return <MarketCapAllocationLoading />
    }

    if (!isLoading && !data?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Market Cap Allocation
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>
            </Box>
        )
    }

    return (
        <Box>
            <Typography
                variant="h2"
                sx={(theme: Theme) => ({
                    lineheight: "25px",
                    letterSpacing: "0.01px",
                    color: theme.palette.text.primary
                })}
            >
                Market Cap Allocation
            </Typography>
            {((modifiedLabelData ?? []).length === 0) ? (
                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>

            ) : (
                <Box
                    sx={{
                        marginTop: "26px",
                        display: "flex",
                        columnGap: "50px",
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            height: "42px",
                        }}
                    >
                        {modifiedLabelData?.map((obj, index) => (
                            <Box
                                key={`${obj?.label}-${index}`}
                                sx={{
                                    backgroundColor: obj?.backgroundColor ?? "",
                                    width: `${obj?.value}%`,
                                    borderRadius: "7px",
                                    zIndex: modifiedLabelData?.length - index,
                                    marginRight: "-10px",
                                }}
                            />
                        ))}
                    </Box>
                    <Grid
                        container
                        sx={{
                            flex: 1,
                            alignItems: "center",
                            flexWrap: "wrap"
                        }}
                    >
                        {modifiedLabelData.map(obj => (
                            <Grid
                                key={obj.label}
                                xs={12}
                                md={6}
                                lg
                                item
                                container
                                sx={{
                                    flexWrap: "nowrap",
                                    columnGap: "10px",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <Grid item>
                                    <CircleIcon
                                        style={{
                                            color: `${obj?.backgroundColor ?? ""}`,
                                            width: '8.79px',
                                            height: '8.79px',
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Typography
                                        variant="overline_400"
                                        sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                                    >
                                        {obj?.label ?? ""}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography
                                        variant="overline_400"
                                        sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                                    >
                                        {!Number.isNaN(Number(obj?.value ?? 0)) ?
                                            Math.round((obj?.value ?? 0) * 100) / 100 : '0'}%
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box >
    )
}